import { MultiPlanSidePanel } from '@/@types/content';
import PanelContentWithErrorBoundary from '@/components/blocks/ErrorFallback/PanelContentWithErrorBoundary';
import LinkBlock from '@/components/blocks/LinkGridBlock/LinkBlock';
import ContentArea from '@/components/cms/ContentArea';
import type { ICreditPlanContext } from '@/contexts/creditPlan/context';
import { expandContent } from '@/lib/helpers/expandChildren';
import { templateString } from '@/utils/helpers';
import Panel, { PanelProps } from '../../Panel';
import Shimmer from '../../Shimmer';
import { getCreditPlanRegionImage } from '../util/CreditPlanHelpers';
import { CreditPlanStatus } from '@/@types/client-api';
import classnames from 'classnames';

export interface MultiCreditPlanSidePanelProps extends Omit<PanelProps, 'size' | 'children'> {
  content?: MultiPlanSidePanel;
  openPanel: ICreditPlanContext['openPanel'];
  jurisdictions: ICreditPlanContext['jurisdictions'];
  isLoading: ICreditPlanContext['isLoading'];
  creditPlans: ICreditPlanContext['creditPlans'];
}

const MultiCreditPlanSidePanel: React.FC<MultiCreditPlanSidePanelProps> = ({
  content,
  openPanel,
  jurisdictions,
  isLoading,
  creditPlans,
  ...panelProps
}) => {
  const { heading, subheading, messagingContentArea } = content ?? {};

  function handleOnClick(jurisdiction: string) {
    return (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      panelProps.onOpenChange?.(false); // mostly for storybook (provider-less)
      openPanel(jurisdiction, undefined, 'multi');
    };
  }

  return (
    <Panel {...panelProps} size="medium">
      <Panel.Content>
        <Panel.Header>
          <div>
            {heading && <h2 className="heading-2">{templateString(heading, {}, true)}</h2>}
            {subheading && (
              <p className="text-2 mt-5 font-normal">{templateString(subheading, {}, true)}</p>
            )}
          </div>
        </Panel.Header>
        <PanelContentWithErrorBoundary>
          <Panel.Body scrollClassName="!pt-6">
            <>
              <ContentArea
                components={expandContent(messagingContentArea)}
                propertyName="MessagingContentArea"
                className="label mb-6 font-medium text-gray-dark"
              />

              <div className="mb-16 flex flex-col gap-4">
                {isLoading && (
                  <>
                    <Shimmer className="h-[80px]" />
                    <Shimmer className="h-[80px]" />
                  </>
                )}

                {!isLoading &&
                  creditPlans?.map(plan => {
                    const juri = jurisdictions?.find(j => j.id === plan.jurisdiction);
                    const isGenerating =
                      !!plan.status &&
                      [CreditPlanStatus.STARTED, CreditPlanStatus.EXECUTING].includes(plan.status);
                    return (
                      <div
                        key={plan.jurisdiction}
                        className={classnames({
                          grayscale: isGenerating
                        })}
                      >
                        <LinkBlock
                          isFeaturedLink={false}
                          link={{
                            href: '#',
                            text: juri?.shortDescription || juri?.name || plan.jurisdiction!,
                            secondaryText: isGenerating ? 'Plan details generating' : undefined,
                            onClick: handleOnClick(plan.jurisdiction!)
                          }}
                          image={{
                            url: getCreditPlanRegionImage(
                              plan.jurisdiction!,
                              juri?.isTransitionalOrNewlyAdmitted ?? false,
                              'landscape'
                            ),
                            altText: juri?.name ?? plan.jurisdiction!
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </>
          </Panel.Body>
        </PanelContentWithErrorBoundary>
      </Panel.Content>
    </Panel>
  );
};

export default MultiCreditPlanSidePanel;
